<template>
  <ul class="pe-biz-digit-flip" ref="digitFlipRef">
    <template v-for="index in displayLength" :key="index">
      <li :class="{ refresh: refreshStates[index - 1] }"></li>
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed, watch, ref, nextTick, onMounted } from 'vue';
import { formatNumber } from './utils';

type Props = {
  num: string | number;
};

const props = defineProps<Props>();
const digitFlipRef = ref<HTMLElement>();

// 格式化后的数字字符串
const formattedNumber = computed(() => formatNumber(props.num));

// 数字字符数组
const currentDigits = ref<string[]>([]);
const targetDigits = ref<string[]>([]);

// 显示的长度（用于控制 DOM 结构）
const displayLength = ref(0);

// 每个位置的刷新状态（用于触发动画）
const refreshStates = ref<boolean[]>([]);

// 动画状态管理
const isAnimating = ref(false);
const hasPendingUpdate = ref(false);
// 保存最新的待处理值，用于在动画结束时检查是否有更新的值
const latestPendingDigits = ref<string[] | null>(null);

// 动画配置
const ANIMATION_DURATION = 800; // 动画时长（毫秒）
const DIGIT_UPDATE_DELAY = 100; // 每个数字更新的延迟（毫秒）

/**
 * 初始化数字显示
 */
const initializeDigits = () => {
  const digits = formattedNumber.value.split('');
  currentDigits.value = [...digits];
  targetDigits.value = [...digits];
  displayLength.value = digits.length;
  refreshStates.value = new Array(digits.length).fill(false);

  nextTick(() => {
    if (!digitFlipRef.value) return;
    const liElements = digitFlipRef.value.querySelectorAll('li');
    digits.forEach((digit, index) => {
      const li = liElements[index];
      if (!li) return;
      const span = document.createElement('span');
      span.textContent = digit;
      li.appendChild(span);
    });
  });
};

/**
 * 检查是否为数字字符
 */
const isDigit = (char: string): boolean => {
  return char !== ',' && char !== undefined;
};

/**
 * 找到第一个需要更新的数字索引（从前往后）
 */
const findFirstChangedIndex = (
  oldDigits: string[],
  newDigits: string[],
  startIndex: number = 0
): number => {
  const minLength = Math.min(oldDigits.length, newDigits.length);
  for (let i = startIndex; i < minLength; i++) {
    if (isDigit(oldDigits[i]) && isDigit(newDigits[i]) && oldDigits[i] !== newDigits[i]) {
      return i;
    }
  }
  return -1;
};

/**
 * 更新单个数字并触发动画
 */
const updateDigit = (
  index: number,
  newDigit: string,
  onStart?: () => void
): Promise<void> => {
  return new Promise((resolve) => {
    const delay = (displayLength.value - index - 1) * DIGIT_UPDATE_DELAY;

    setTimeout(() => {
      if (!digitFlipRef.value) {
        resolve();
        return;
      }

      const liElements = digitFlipRef.value.querySelectorAll('li');
      const li = liElements[index] as HTMLElement | undefined;

      if (!li) {
        resolve();
        return;
      }

      // 创建新的数字元素
      const span = document.createElement('span');
      span.textContent = newDigit;
      li.appendChild(span);

      // 触发动画
      refreshStates.value[index] = true;

      // 调用开始回调（在动画开始时）
      onStart?.();

      // 监听动画完成
      const handleAnimationEnd = (event: AnimationEvent) => {
        if (event.animationName !== 'anis') return;

        li.removeEventListener('animationend', handleAnimationEnd);

        // 重置 transform
        if (li instanceof HTMLElement) {
          li.style.transform = 'translateY(0)';
        }

        // 移除 refresh 状态
        refreshStates.value[index] = false;

        // 清理旧节点，只保留最后一个
        requestAnimationFrame(() => {
          const children = Array.from(li.children);
          if (children.length > 1) {
            for (let i = 0; i < children.length - 1; i++) {
              li.removeChild(children[i]);
            }
          }

          // 清除手动设置的 transform
          if (li instanceof HTMLElement) {
            li.style.transform = '';
          }

          resolve();
        });
      };

      li.addEventListener('animationend', handleAnimationEnd);

      // 超时保护
      setTimeout(() => {
        li.removeEventListener('animationend', handleAnimationEnd);
        if (refreshStates.value[index]) {
          handleAnimationEnd({ animationName: 'anis' } as AnimationEvent);
        }
      }, ANIMATION_DURATION + 100);
    }, delay);
  });
};

/**
 * 执行数字滚动动画
 */
const animateDigits = async (isCarryOver: boolean = false): Promise<void> => {
  const oldDigits = currentDigits.value;
  // 保存动画开始时的目标值，避免动画过程中 targetDigits.value 被更新导致状态不一致
  const animationStartTarget = [...targetDigits.value];
  const newDigits = animationStartTarget;
  const diffLength = newDigits.length - oldDigits.length;

  // 确定目标长度和要比较的数组
  const targetLength = isCarryOver ? oldDigits.length : newDigits.length;
  const compareDigits = isCarryOver
    ? newDigits.slice(diffLength)
    : newDigits;

  // 调试日志：检查输入数据
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[DigitFlip] animateDigits', {
  //     isCarryOver,
  //     oldDigits: oldDigits.join(''),
  //     newDigits: newDigits.join(''),
  //     compareDigits: compareDigits.join(''),
  //     diffLength,
  //     targetLength,
  //   });
  // }

  // 找到第一个变化的索引
  const firstChangedIndex = findFirstChangedIndex(oldDigits, compareDigits);

  if (firstChangedIndex === -1) {
    // 没有需要更新的数字
    isAnimating.value = false;
    if (isCarryOver) {
      await addNewDigit();
    } else if (hasPendingUpdate.value) {
      hasPendingUpdate.value = false;
      // 重新读取最新的 props 值，确保使用最新的数字
      targetDigits.value = formattedNumber.value.split('');
      // 更新 currentDigits 为当前显示的值
      currentDigits.value = [...targetDigits.value];
      processNumberChange();
    }
    return;
  }

  // 如果是进位操作，先准备 DOM 结构（但不添加 placeholder-index）
  let indexOffset = 0; // 索引偏移量，用于调整更新时的索引
  if (isCarryOver) {
    await addNewDigit(false);
    // 计算索引偏移量：新数字比旧数字多出的长度
    indexOffset = diffLength;
  }

  // 收集需要更新的数字索引和值
  const updates: Array<{ index: number; digit: string }> = [];
  for (let i = targetLength - 1; i >= 0; i--) {
    const oldDigit = oldDigits[i];
    const newDigit = compareDigits[i];

    // 确保 oldDigit 和 newDigit 都存在且有效
    // 跳过逗号，只处理数字
    if (
      oldDigit !== undefined &&
      newDigit !== undefined &&
      isDigit(oldDigit) &&
      isDigit(newDigit) &&
      oldDigit !== newDigit
    ) {
      // 调整索引：如果是进位操作，需要加上偏移量
      const adjustedIndex = i + indexOffset;
      updates.push({ index: adjustedIndex, digit: newDigit });
      
      // 调试日志
      // if (process.env.NODE_ENV === 'development') {
      //   console.log(`[DigitFlip] 需要更新: 位置 ${i} -> ${adjustedIndex}, ${oldDigit} -> ${newDigit}`);
      // }
    }
  }

  // 调试日志：检查更新列表
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[DigitFlip] updates', updates);
  // }

  if (updates.length === 0) {
    isAnimating.value = false;
    if (isCarryOver) {
      // 如果没有需要更新的数字，直接添加 placeholder-index
      await addNewDigit(true);
    } else if (hasPendingUpdate.value && latestPendingDigits.value) {
      hasPendingUpdate.value = false;
      // 使用保存的最新值
      targetDigits.value = [...latestPendingDigits.value];
      latestPendingDigits.value = null;
      // 更新 currentDigits 为当前显示的值
      currentDigits.value = [...oldDigits];
      processNumberChange();
    } else {
      hasPendingUpdate.value = false;
      latestPendingDigits.value = null;
    }
    return;
  }

  // 找到第一个需要滚动的数字（调整后索引最小的，对应最前面的数字）
  // 因为数字是从后往前滚动的，所以第一个滚动的数字是索引最大的
  // 但用户要求当滚动到第一个数字（最前面的）时，才开始展示进位数字1
  // 所以需要找到调整后索引最小的更新（对应最前面的数字）
  const firstUpdateIndex = isCarryOver 
    ? Math.min(...updates.map(({ index }) => index))
    : Math.max(...updates.map(({ index }) => index));

  // 执行所有更新
  const promises = updates.map(({ index, digit }) => {
    // 如果是第一个数字（最前面的）且是进位操作，在开始滚动时添加 placeholder-index 和 "1"
    const onStart = isCarryOver && index === firstUpdateIndex ? () => {
      addPlaceholderIndex();
    } : undefined;
    return updateDigit(index, digit, onStart);
  });

  // 等待所有动画完成
  await Promise.all(promises);

  // 所有数字更新完成，更新 currentDigits 为动画开始时的目标值（动画实际完成的值）
  // 注意：必须在所有动画完成后才更新，确保状态一致性
  currentDigits.value = [...animationStartTarget];
  
  // 调试日志：检查更新后的状态
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[DigitFlip] 动画完成，currentDigits 已更新为:', currentDigits.value.join(''));
  // }
  
  // 重置动画状态
  isAnimating.value = false;
  
  // 如果有待处理的更新，检查是否有更新的值需要处理
  if (hasPendingUpdate.value && latestPendingDigits.value) {
    hasPendingUpdate.value = false;
    // 使用保存的最新值
    targetDigits.value = [...latestPendingDigits.value];
    latestPendingDigits.value = null;
    // 调试日志：检查更新后的状态
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[DigitFlip] 动画完成，有待处理更新，使用保存的最新值:', targetDigits.value.join(''));
    // }
    // 继续处理更新（此时 currentDigits 已经是动画完成时的值）
    processNumberChange();
  } else {
    // 清除待处理状态
    hasPendingUpdate.value = false;
    latestPendingDigits.value = null;
  }
};

/**
 * 添加 placeholder-index class 和进位数字 "1"（在第一个位置）
 */
const addPlaceholderIndex = (): void => {
  if (!digitFlipRef.value) return;
  const liElements = digitFlipRef.value.querySelectorAll('li');
  const firstLi = liElements[0] as HTMLElement | undefined;
  if (!firstLi) return;
  
  // 清除之前设置的宽度样式
  firstLi.style.width = '';
  firstLi.style.overflow = '';
  
  // 如果还没有添加 "1"，先添加
  if (firstLi.children.length === 0) {
    const span1 = document.createElement('span');
    span1.textContent = '1';
    firstLi.appendChild(span1);
  }
  
  // 添加 placeholder-index class（这会触发动画，从宽度 0 变为 20px）
  if (!firstLi.classList.contains('placeholder-index')) {
    firstLi.classList.add('placeholder-index');
  }
};

/**
 * 添加新位数（进位时使用）
 */
const addNewDigit = async (addPlaceholder: boolean = true): Promise<void> => {
  const diffLength = targetDigits.value.length - currentDigits.value.length;

  // 更新显示长度
  displayLength.value = targetDigits.value.length;

  // 扩展 refresh 数组
  const oldLength = refreshStates.value.length;
  if (targetDigits.value.length > oldLength) {
    refreshStates.value = [
      ...refreshStates.value,
      ...new Array(targetDigits.value.length - oldLength).fill(false),
    ];
  }

  await nextTick();

  if (!digitFlipRef.value) return;

  const liElements = digitFlipRef.value.querySelectorAll('li');
  const firstLi = liElements[0] as HTMLElement | undefined;
  const lastLi = liElements[liElements.length - 1] as HTMLElement | undefined;

  if (!firstLi || !lastLi || liElements.length <= 1) return;

  // 处理进位逻辑
  if (diffLength === 2 && targetDigits.value[1] === ',') {
    // 特殊情况：进位时第二位是逗号（如 999 -> 1,000）
    // 每个 li 中的内容整体向后移动两位
    for (let i = liElements.length - 1; i >= 2; i--) {
      const currentLi = liElements[i] as HTMLElement;
      const prevLi = liElements[i - 2] as HTMLElement;
      while (prevLi.firstChild) {
        currentLi.appendChild(prevLi.firstChild);
      }
    }

    // 在第二个 li 中插入逗号
    if (targetDigits.value[1] === ',') {
      const commaSpan = document.createElement('span');
      commaSpan.textContent = ',';
      (liElements[1] as HTMLElement).appendChild(commaSpan);
    }

    // 清空第一个 li 的内容
    firstLi.innerHTML = '';
    
    // 如果需要添加 placeholder-index，立即添加 "1" 和 class
    if (addPlaceholder) {
      const span1 = document.createElement('span');
      span1.textContent = '1';
      firstLi.appendChild(span1);
      firstLi.classList.add('placeholder-index');
    } else {
      // 如果不需要添加 placeholder-index，设置宽度为 0，避免显示空位
      firstLi.style.width = '0';
      firstLi.style.overflow = 'hidden';
    }
  } else {
    // 一般情况：每个 li 中的内容整体向后移动一位
    for (let i = liElements.length - 1; i > 0; i--) {
      const currentLi = liElements[i] as HTMLElement;
      const prevLi = liElements[i - 1] as HTMLElement;
      while (prevLi.firstChild) {
        currentLi.appendChild(prevLi.firstChild);
      }
    }

    // 清空第一个 li 的内容
    firstLi.innerHTML = '';
    
    // 如果需要添加 placeholder-index，立即添加 "1" 和 class
    if (addPlaceholder) {
      const span1 = document.createElement('span');
      span1.textContent = '1';
      firstLi.appendChild(span1);
      firstLi.classList.add('placeholder-index');
    } else {
      // 如果不需要添加 placeholder-index，设置宽度为 0，避免显示空位
      firstLi.style.width = '0';
      firstLi.style.overflow = 'hidden';
    }
  }

  // 只有在添加 placeholder-index 时才更新当前数字数组
  // 如果只是准备 DOM 结构，不更新 currentDigits（因为数字还没滚动完成）
  if (addPlaceholder) {
    // 重置动画状态
    isAnimating.value = false;

    if (hasPendingUpdate.value && latestPendingDigits.value) {
      hasPendingUpdate.value = false;
      // 使用保存的最新值
      targetDigits.value = [...latestPendingDigits.value];
      latestPendingDigits.value = null;
      // 更新 currentDigits 为当前显示的值（动画完成时的值）
      // 注意：这里需要根据实际情况更新，如果是进位操作，currentDigits 应该已经是正确的了
      processNumberChange();
    } else {
      // 更新 currentDigits 为当前显示的值
      currentDigits.value = [...targetDigits.value];
      hasPendingUpdate.value = false;
      latestPendingDigits.value = null;
    }
  }
};

/**
 * 直接更新数字（无动画，用于大幅变化）
 */
const updateDigitsDirectly = (): void => {
  displayLength.value = targetDigits.value.length;
  refreshStates.value = new Array(targetDigits.value.length).fill(false);

  nextTick(() => {
    if (!digitFlipRef.value) return;

    const liElements = digitFlipRef.value.querySelectorAll('li');
    targetDigits.value.forEach((digit, index) => {
      const li = liElements[index] as HTMLElement | undefined;
      if (!li) return;

      li.innerHTML = '';
      const span = document.createElement('span');
      span.textContent = digit;
      li.appendChild(span);
    });

    currentDigits.value = [...targetDigits.value];
  });
};

/**
 * 处理数字变化
 */
const processNumberChange = async (): Promise<void> => {
  // 前一个动画未播放完成，保存最新的值并记录状态
  if (isAnimating.value) {
    hasPendingUpdate.value = true;
    // 保存最新的待处理值
    latestPendingDigits.value = [...targetDigits.value];
    return;
  } else {
    hasPendingUpdate.value = false;
    latestPendingDigits.value = null;
  }

  const diffLength = targetDigits.value.length - currentDigits.value.length;

  if (diffLength === 0) {
    // 位数相同，执行滚动动画
    isAnimating.value = true;
    await animateDigits(false);
  } else if (diffLength === 1 || (diffLength === 2 && targetDigits.value[1] === ',')) {
    // 位数增加：先滚动到目标值，再添加新位数
    isAnimating.value = true;
    await animateDigits(true);
  } else {
    // 位数变化较大或减少：直接更新，无动画
    isAnimating.value = false;
    hasPendingUpdate.value = false;
    latestPendingDigits.value = null;
    updateDigitsDirectly();
  }
};

// 监听数字变化
watch(
  () => props.num,
  () => {
    targetDigits.value = formattedNumber.value.split('');
    // console.log('====================================');
    // console.log('props.num', props.num);
    // console.log('formattedNumber.value', formattedNumber.value);
    // console.log('targetDigits.value', targetDigits.value);
    // console.log('====================================');
    processNumberChange();
  }
);

// 初始化
onMounted(() => {
  initializeDigits();
});
</script>

<style scoped lang="scss">
.pe-biz-digit-flip {
  ul,
  li {
    list-style: none;
    padding: 0px;
    margin: 0px;
  }

  display: flex;
  flex-direction: row;
  color: #040b29;
  font-size: 36px;
  line-height: 34px;
  padding-right: 5px;
  height: 34px;
  overflow: hidden;
  font-weight: 400;

  li {
    align-items: center;
    display: flex;
    flex-direction: column;
    width: 24px;
    text-align: center;

    span {
      display: inline-block;
      width: 100%;
      font-style: inherit;
      height: 100%;
    }

    &:nth-last-child(4n) {
      width: 10px;
    }
  }

  & > li.refresh {
    animation: anis 0.8s ease 1;
    animation-fill-mode: both;
  }

  @keyframes anis {
    0% {
      opacity: 0;
      transform: translateY(0%);
    }

    100% {
      opacity: 1;
      transform: translateY(-100%);
    }
  }

  .placeholder-index {
    overflow: hidden;
    animation: liShow 0.5s ease-out 0.2s;
    animation-fill-mode: both;
  }

  @keyframes liShow {
    0% {
      opacity: 0;
      width: 0;
      transform: translateY(100%);
    }

    100% {
      opacity: 1;
      // width: fit-content;
      transform: translateY(0%);
    }
  }
}
</style>
